import { Timestamp } from "firebase/firestore";

/**
 * Represents various types of date inputs that can be processed by the date utilities.
 */
export type DateInput =
  | string
  | Date
  | Timestamp
  | { seconds: number }
  | null
  | undefined;

/**
 * Represents the format options for date formatting functions.
 * Can be a predefined format string or custom Intl.DateTimeFormatOptions.
 */
export type DateFormatType =
  | "short"
  | "medium"
  | "long"
  | "full"
  | Intl.DateTimeFormatOptions;

/**
 * Converts any supported date format to a JavaScript Date object.
 */
export const toDate = (date: DateInput): Date | null => {
  if (!date) return null;

  try {
    if (date instanceof Date) return date;
    if (date instanceof Timestamp) return date.toDate();
    if (typeof date === "string") return new Date(date);
    if (typeof date === "object" && "seconds" in date) {
      return new Date(date.seconds * 1000);
    }
  } catch (error) {
    console.error("Error converting date:", error);
  }

  return null;
};

/**
 * Resolves a format type to Intl.DateTimeFormatOptions.
 */
export const resolveFormatOptions = (
  format: DateFormatType,
): Intl.DateTimeFormatOptions => {
  if (typeof format === "string") {
    switch (format) {
      case "short":
        return { month: "numeric", day: "numeric", year: "2-digit" };
      case "medium":
        return { month: "short", day: "numeric", year: "numeric" };
      case "long":
        return { month: "long", day: "numeric", year: "numeric" };
      case "full":
        return {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        };
      default:
        return { month: "short", day: "numeric", year: "numeric" }; // Default to medium
    }
  }
  return format;
};

/**
 * Formats a single date according to the specified format.
 */
export const formatDate = (
  dateInput: DateInput,
  format: DateFormatType = "medium",
): string => {
  const date = toDate(dateInput);
  if (!date) return "";

  try {
    const options = resolveFormatOptions(format);
    return new Intl.DateTimeFormat("en-US", options).format(date);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

/**
 * Formats a date range.
 */
export const formatDateRange = (
  startDateInput: DateInput,
  endDateInput: DateInput,
  format: DateFormatType = "medium",
): string => {
  const startDate = toDate(startDateInput);
  if (!startDate) return "";

  const endDate = toDate(endDateInput);
  if (!endDate) return formatDate(startDate, format);

  // Same day case
  if (startDate.getTime() === endDate.getTime()) {
    return formatDate(startDate, format);
  }

  // For same month and year, we can use a special format
  if (
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getFullYear() === endDate.getFullYear()
  ) {
    // Format just the month
    const baseOptions = resolveFormatOptions(format);
    const monthOptions: Intl.DateTimeFormatOptions = {
      month:
        typeof format === "string"
          ? format === "short"
            ? "numeric"
            : format === "medium"
              ? "short"
              : "long"
          : baseOptions.month || "long",
    };

    const monthFormatter = new Intl.DateTimeFormat("en-US", monthOptions);
    const month = monthFormatter.format(startDate);

    // Get the days and year
    return `${month} ${startDate.getDate()}-${endDate.getDate()}, ${startDate.getFullYear()}`;
  }

  // Different months or years - use full formatting for both dates
  return `${formatDate(startDate, format)} - ${formatDate(endDate, format)}`;
};

/**
 * Formats a 24-hour time string (HH:mm) to localized 12-hour format
 * @param timeString - Time in HH:mm format (e.g., "18:00")
 * @returns Formatted time (e.g., "6:00 PM")
 */
export const formatTime = (timeString: string | undefined | null): string => {
  if (!timeString || !timeString.trim()) {
    return "";
  }

  const [hours, minutes] = timeString.split(":").map(Number);

  if (
    isNaN(hours) ||
    isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return timeString; // Return original if invalid
  }

  const period = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, "0");

  return `${displayHour}:${displayMinutes} ${period}`;
};

/**
 * Formats time as a string, either from explicit time strings or from a date object.
 */
export const formatTimeRange = (
  startTime?: string,
  endTime?: string,
  startDate?: DateInput,
  includeTime = false,
): string => {
  try {
    if (startTime) {
      const formattedStart = formatTime(startTime);
      if (endTime) {
        const formattedEnd = formatTime(endTime);
        return `${formattedStart} - ${formattedEnd}`;
      }
      return formattedStart;
    }

    if (includeTime && startDate) {
      const date = toDate(startDate);
      if (!date || isNaN(date.getTime())) return "";

      try {
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      } catch (error) {
        console.error("Error formatting time:", error);
        return "";
      }
    }

    return "";
  } catch (error) {
    console.error("Unexpected error in formatTimeRange:", error);
    return "";
  }
};

/**
 * Checks if a date is in the past
 */
export const isPastDate = (dateInput: DateInput): boolean => {
  const date = toDate(dateInput);
  return date ? date < new Date() : false;
};

/**
 * Calculates days between two dates
 */
export const getDaysDifference = (
  startDateInput: DateInput,
  endDateInput: DateInput = new Date(),
): number => {
  const startDate = toDate(startDateInput);
  const endDate = toDate(endDateInput);

  if (!startDate || !endDate) return 0;

  // Reset time part to compare days only (using UTC to avoid timezone issues)
  const start = new Date(
    Date.UTC(
      startDate.getUTCFullYear(),
      startDate.getUTCMonth(),
      startDate.getUTCDate(),
    ),
  );
  const end = new Date(
    Date.UTC(
      endDate.getUTCFullYear(),
      endDate.getUTCMonth(),
      endDate.getUTCDate(),
    ),
  );

  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Normalizes a time string to HH:mm format (24-hour).
 * Accepts various formats:
 * - "6:00 PM" or "6:00 pm" (12-hour with meridian)
 * - "18:00" (24-hour)
 * - "6:00" (24-hour without zero-padding)
 *
 * @param timeString - The time string to normalize
 * @returns The time in HH:mm format, or empty string if invalid
 *
 * @example
 * normalizeTimeString("6:00 PM") // "18:00"
 * normalizeTimeString("9:00 AM") // "09:00"
 * normalizeTimeString("18:00")   // "18:00"
 * normalizeTimeString("6:00")    // "06:00"
 */
export const normalizeTimeString = (timeString: string | undefined): string => {
  if (!timeString || timeString.trim() === "") {
    return "";
  }

  const trimmed = timeString.trim().toLowerCase();

  // Check if it's already in HH:mm format (24-hour)
  const hhmmMatch = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmmMatch) {
    const hours = parseInt(hhmmMatch[1], 10);
    const minutes = parseInt(hhmmMatch[2], 10);

    // Validate hours (0-23) and minutes (0-59)
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return "";
    }

    return `${hours.toString().padStart(2, "0")}:${hhmmMatch[2]}`;
  }

  // Try to match 12-hour format like "6:00 PM", "6:00 pm", "6:00PM"
  const meridianMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
  if (meridianMatch) {
    let hours = parseInt(meridianMatch[1], 10);
    const minutes = parseInt(meridianMatch[2], 10);
    const meridian = meridianMatch[3];

    // Validate 12-hour format (1-12) and minutes (0-59)
    if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
      return "";
    }

    if (meridian === "pm" && hours !== 12) {
      hours += 12;
    } else if (meridian === "am" && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, "0")}:${meridianMatch[2]}`;
  }

  // If we can't parse it, return empty string
  return "";
};
